import { Formik, useFormikContext } from "formik";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import * as Yup from "yup";
import { valListAction } from "../../redux/actions/general-action";
import { productInventoryAction } from "../../redux/actions/product-action";
import AddButton from "../buttons/button";
import CheckBox from "../forms/check-box-field";
import InputField from "../forms/input-field";
import SelectField from "../forms/select-field";
import errorToast from "../helper-components/error-toast";
import successToast from "../helper-components/success-toast";

// The purchasing min/max and the slow & fast moving fields must always reach
// the API as numbers — the number inputs, pasted values and the item-category
// defaults can all leave them as strings.
const NUMERIC_FIELDS = [
  "item_minimun_purchasing",
  "item_maximum_purchasing",
  "item_minimum_qty",
  "item_maximum_qty",
  "item_minimum_days",
  "item_maximum_days",
];

const toNumberOrNull = (value) => {
  if (value === "" || value === null || value === undefined) return null;
  const num = Number(value);
  return Number.isNaN(num) ? null : num;
};

const withNumericFields = (values) => ({
  ...values,
  ...Object.fromEntries(
    NUMERIC_FIELDS.map((field) => [field, toNumberOrNull(values?.[field])])
  ),
});

const InventoryTab = ({
  productId,
  uomList,
  setFormValues,
  formValues,
  setCurrentTab,
  product,
  disabled = false,
  itemGeneralData = {},
  selectedItemCategory,
  onTabChange,
}) => {
  const dispatch = useDispatch();
  const [valList, setvalList] = useState([]);

  // Inventory tab specific state for navigation blocking
  const [inventoryHasUnsavedChanges, setInventoryHasUnsavedChanges] =
    useState(false);
  const [inventoryIsFormSaved, setInventoryIsFormSaved] = useState(false);

  useEffect(() => {
    dispatch(valListAction(onvalSuccess, onvalError));
  }, []);

  const onvalSuccess = (data) => {
    setvalList(
      data?.data?.scheduleList?.map((item) => {
        return { label: item?.valuationMethod, value: item?.valuationMethod };
      })
    );
  };
  const onvalError = (data) => {
    errorToast(data?.message);
  };

  const initialValues = {
    item_id: product?.item_id ?? productId,
    item_set_gl_account: product?.item_set_gl_account ?? "",
    item_uom_name: product?.item_uom_name ?? "",
    item_weight: product?.item_weight ?? null,
    item_valuation_method: product?.item_valuation_method ?? "",
    item_cost: product?.item_cost ?? null,
    item_purchasing_uom: product?.item_purchasing_uom ?? "",
    item_minimun_purchasing: toNumberOrNull(product?.item_minimun_purchasing),
    item_maximum_purchasing: toNumberOrNull(product?.item_maximum_purchasing),
    item_minimum_qty: toNumberOrNull(product?.item_minimum_qty),
    item_maximum_qty: toNumberOrNull(product?.item_maximum_qty),
    item_minimum_days: toNumberOrNull(product?.item_minimum_days),
    item_maximum_days: toNumberOrNull(product?.item_maximum_days),
    manage_inventory_by_warehouse:
      product?.manage_inventory_by_warehouse ?? false,
  };

  // A Maximum field only needs checking against its own Minimum, and only
  // when both are actually filled in — either one left blank is a valid,
  // unbounded configuration.
  const maxNotLessThanMin = (minField, message) =>
    Yup.number()
      .nullable()
      .test("max-gte-min", message, function (max) {
        const min = this.parent?.[minField];
        if (max === null || max === undefined || max === "") return true;
        if (min === null || min === undefined || min === "") return true;
        return Number(max) >= Number(min);
      });

  const validationSchema = Yup.object().shape({
    item_purchasing_uom: Yup.string().required("required"),
    item_maximum_purchasing: maxNotLessThanMin(
      "item_minimun_purchasing",
      "Maximum cannot be less than Minimum"
    ),
    item_maximum_qty: maxNotLessThanMin(
      "item_minimum_qty",
      "Maximum quantity cannot be less than Minimum quantity"
    ),
    item_maximum_days: maxNotLessThanMin(
      "item_minimum_days",
      "Maximum days cannot be less than Minimum days"
    ),
  });

  // Inventory tab change tracker and navigation blocking
  const FormChangeTracker = () => {
    const { values } = useFormikContext();

    useEffect(() => {
      // Don't track changes if form was just saved
      if (inventoryIsFormSaved) {
        setInventoryHasUnsavedChanges(false);
        if (onTabChange) {
          onTabChange(false);
        }
        return;
      }

      // Compare with initial values
      const hasChanges =
        JSON.stringify(initialValues) !== JSON.stringify(values);
      setInventoryHasUnsavedChanges(hasChanges);

      // Notify parent component about changes
      if (onTabChange) {
        onTabChange(hasChanges);
      }

      console.log("=== INVENTORY TAB CHANGE TRACKER ===");
      console.log("Inventory has changes:", hasChanges);
      console.log("Initial values:", initialValues);
      console.log("Current values:", values);
    }, [values, inventoryIsFormSaved, onTabChange]);

    // Navigation blocking is now handled by parent component (EditProduct)

    return null;
  };

  // Fills the slow/fast moving fields from the selected item category
  // whenever the category changes, but only for fields that are still null.
  const CategoryDefaultsSync = () => {
    const { values, setFieldValue } = useFormikContext();

    useEffect(() => {
      if (!selectedItemCategory) return;

      const categoryFields = {
        item_minimum_qty: selectedItemCategory?.item_minimum_qty,
        item_maximum_qty: selectedItemCategory?.item_maximum_qty,
        item_minimum_days: selectedItemCategory?.item_minimum_days,
        item_maximum_days: selectedItemCategory?.item_maximum_days,
      };

      Object.entries(categoryFields).forEach(([field, categoryValue]) => {
        if (values?.[field] === null || values?.[field] === undefined) {
          setFieldValue(field, toNumberOrNull(categoryValue));
        }
      });
    }, [selectedItemCategory?.id]);

    return null;
  };

  const innerOnSubmit = (values) => {
    if (JSON.stringify(initialValues) === JSON.stringify(values)) {
      return errorToast("No changes");
    }
    const inventoryValues = withNumericFields(values);

    setFormValues({
      ...formValues,
      inventory: inventoryValues,
    });

    // Reset inventory form state after successful submission
    setInventoryHasUnsavedChanges(false);
    setInventoryIsFormSaved(true);
    dispatch(
      productInventoryAction(
        {
          itemGeneralData: itemGeneralData,
          itemInventoryData: inventoryValues,
        },
        onSuccess,
        onError
      )
    );
  };

  const onSuccess = (data) => {
    successToast(data?.message);
    setCurrentTab("Planning");
  };
  const onError = (data) => { };

  return (
    <>
      <Formik
        enableReinitialize={true}
        initialValues={formValues?.inventory ?? initialValues}
        validationSchema={validationSchema}
        onSubmit={innerOnSubmit}
      >
        {({ values, handleSubmit, errors, setFieldTouched }) => {
          return (
            // <Form>
            <>
              <FormChangeTracker />
              <CategoryDefaultsSync />
              <div className="flex flex-wrap mt-3">
                <div className="w-full 2xl:w-[35%] lg:w-1/2 px-5 xl:px-10">
                  <SelectField
                    isDisabled={disabled}
                    placeholder="Select"
                    options={serialBatchesOptns}
                    label="Set G/L Account by"
                    id="item_set_gl_account"
                    name="item_set_gl_account"
                    isSearchable={true}
                    errMsgContainerClass="ps-2"
                    isRequired={false}
                    labelWidth="9rem"
                  />
                  <SelectField
                    isDisabled={disabled}
                    placeholder="Select uom"
                    options={uomList}
                    label="UoM Name"
                    id="item_uom_name"
                    name="item_uom_name"
                    isSearchable={true}
                    errMsgContainerClass="ps-2"
                    isRequired={false}
                    labelWidth="9rem"
                  />
                  <InputField
                    isDisabled={disabled}
                    type="number"
                    name="item_weight"
                    placeholder="xxxxxxx"
                    label="Weight"
                    isRequired={false}
                    labelWidth="9rem"
                  />
                </div>
                <div className="w-full 2xl:w-[35%] lg:w-1/2 px-5 xl:px-10 mt-10 lg:mt-0">
                  <SelectField
                    isDisabled={disabled}
                    placeholder=""
                    options={valList}
                    label="Valuation Method"
                    id="item_valuation_method"
                    name="item_valuation_method"
                    isSearchable={true}
                    errMsgContainerClass="ps-2"
                    isRequired={false}
                    labelWidth="9rem"
                  />

                  <InputField
                    isDisabled={disabled}
                    type="number"
                    name="item_cost"
                    placeholder="xxxxxxx"
                    label="Item Cost"
                    isRequired={false}
                    labelWidth="9rem"
                  />
                </div>
              </div>
              <div className="flex flex-wrap mt-10">
                <div className="w-full 2xl:w-[35%] lg:w-1/2 px-5 xl:px-10">
                  <SelectField
                    isDisabled={disabled}
                    placeholder=""
                    options={uomList}
                    label="Purchasing UoM"
                    id="item_purchasing_uom"
                    name="item_purchasing_uom"
                    isSearchable={true}
                    errMsgContainerClass="ps-2"
                    isRequired={true}
                    labelWidth="9rem"
                  />

                  <InputField
                    isDisabled={disabled}
                    type="number"
                    name="item_minimun_purchasing"
                    placeholder=""
                    label="Minimum"
                    isRequired={false}
                    labelWidth="9rem"
                    // Re-validating only marks the field that was blurred as
                    // touched, so editing Minimum after Maximum was already
                    // set would leave the Maximum error hidden until Save.
                    // Touch both so the Max/Min mismatch shows immediately,
                    // from whichever side the user edits.
                    onBlur={() => {
                      setFieldTouched("item_minimun_purchasing", true);
                      setFieldTouched("item_maximum_purchasing", true);
                    }}
                  />

                  <InputField
                    isDisabled={disabled}
                    type="number"
                    name="item_maximum_purchasing"
                    placeholder=""
                    label="Maximum"
                    isRequired={false}
                    labelWidth="9rem"
                    onBlur={() => {
                      setFieldTouched("item_minimun_purchasing", true);
                      setFieldTouched("item_maximum_purchasing", true);
                    }}
                  />
                  <div
                    style={{
                      fontSize: "14px",
                      fontWeight: 600,
                      margin: "16px 0 8px",
                    }}
                  >
                    Slow & Fast moving configuration
                  </div>

                  <div
                    style={{
                      fontSize: "13px",
                      fontWeight: 600,
                      margin: "8px 0 4px",
                    }}
                  >
                    Quantity
                  </div>
                  <div className="flex gap-4">
                    <InputField
                      isDisabled={disabled}
                      type="number"
                      name="item_minimum_qty"
                      placeholder=""
                      label="Minimum"
                      isRequired={false}
                      labelWidth="5rem"
                      containerClass="mb-2 position-relative w-1/2"
                      onBlur={() => {
                        setFieldTouched("item_minimum_qty", true);
                        setFieldTouched("item_maximum_qty", true);
                      }}
                    />

                    <InputField
                      isDisabled={disabled}
                      type="number"
                      name="item_maximum_qty"
                      placeholder=""
                      label="Maximum"
                      isRequired={false}
                      labelWidth="5rem"
                      containerClass="mb-2 position-relative w-1/2"
                      onBlur={() => {
                        setFieldTouched("item_minimum_qty", true);
                        setFieldTouched("item_maximum_qty", true);
                      }}
                    />
                  </div>

                  <div
                    style={{
                      fontSize: "13px",
                      fontWeight: 600,
                      margin: "8px 0 4px",
                    }}
                  >
                    Days
                  </div>
                  <div className="flex gap-4">
                    <InputField
                      isDisabled={disabled}
                      type="number"
                      name="item_minimum_days"
                      placeholder=""
                      label="Minimum"
                      isRequired={false}
                      labelWidth="5rem"
                      containerClass="mb-2 position-relative w-1/2"
                      onBlur={() => {
                        setFieldTouched("item_minimum_days", true);
                        setFieldTouched("item_maximum_days", true);
                      }}
                    />

                    <InputField
                      isDisabled={disabled}
                      type="number"
                      name="item_maximum_days"
                      placeholder=""
                      label="Maximum"
                      isRequired={false}
                      labelWidth="5rem"
                      containerClass="mb-2 position-relative w-1/2"
                      onBlur={() => {
                        setFieldTouched("item_minimum_days", true);
                        setFieldTouched("item_maximum_days", true);
                      }}
                    />
                  </div>
                </div>
                <div className="w-full 2xl:w-[35%] lg:w-1/2 px-5 xl:px-10 mt-10 lg:mt-0">
                  <CheckBox
                    isDisabled={disabled}
                    conClass="flex flex-col  gap-10"
                    items={itemoptions}
                  />
                </div>
              </div>
              {!disabled && (
                <div
                  style={{
                    bottom: "0",
                  }}
                  className="flex justify-end w-full px-10 mb-5x sticky bg-white py-4 invoice-bottom-container"
                >
                  <AddButton
                    onclkFn={handleSubmit}
                    prefixIcon={false}
                    text="Save"
                    type="submit"
                  />
                </div>
              )}
            </>
          );
        }}
      </Formik>
    </>
  );
};

export default InventoryTab;

export const serialBatchesOptns = [
  { label: "Item Group", value: "item_group" },
];
const itemoptions = [
  {
    label: "Manage inventory by Warehouse",
    name: "manage_inventory_by_warehouse",
    isRequired: false,
  },
];
